/**
 * TanStack Query setup for mockExam data fetching.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { mockExamService } from "@/infrastructure/services";
import { TMockExamSubmitRequest } from "@/infrastructure/adapters/mock-exam/mock-exam.types";
import { QUERY_KEYS } from "@/constants/queryKeys";

/**
 * Query for the list of active mock exams.
 * Intentionally uncached (staleTime/gcTime 0, always refetch on mount) so the
 * list — and each exam's「前回の試験」— never shows stale data.
 */
export const useMockExamListQuery = (options?: { enabled?: boolean }) =>
  useQuery({
    queryKey: [QUERY_KEYS.MOCK_EXAM.LIST],
    queryFn: () => mockExamService.getMockExamList(),
    enabled: options?.enabled ?? true,
    staleTime: 0,
    gcTime: 0,
  });

/** Query for one mockExam overview (metadata + attempts history) */
export const useMockExamDetailQuery = (mockExamId: number, options?: { enabled?: boolean }) =>
  useQuery({
    queryKey: [QUERY_KEYS.MOCK_EXAM.DETAIL, mockExamId],
    queryFn: () => mockExamService.getMockExamDetail(mockExamId),
    enabled: !Number.isNaN(mockExamId) && mockExamId > 0 && (options?.enabled ?? true),
  });

/** Mutation: Start a new attempt for the specified mockExam */
export const useCreateMockExamSessionMutation = () => {
  return useMutation({
    mutationFn: (mockExamId: number) => mockExamService.createMockExamSession(mockExamId),
  });
};

/** Mutation: Submit all mockExam answers at once */
export const useSubmitMockExamMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (req: TMockExamSubmitRequest) => mockExamService.submitMockExam(req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.MOCK_EXAM.LIST] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.MOCK_EXAM.DETAIL] });
    },
  });
};

/** Query for mockExam review by attempt ID */
export const useMockExamResultQuery = (attemptId: number, options?: { enabled?: boolean }) =>
  useQuery({
    queryKey: [QUERY_KEYS.MOCK_EXAM.RESULT, attemptId],
    queryFn: () => mockExamService.getMockExamResult(attemptId),
    enabled: !Number.isNaN(attemptId) && attemptId > 0 && (options?.enabled ?? true),
  });
